import React, { useMemo } from 'react';
import { Grid2, Typography } from '@mui/material';
import { withStyles } from '@mui/styles';
import { useFormContext } from 'react-hook-form';
import { COLORS } from '../../../utils/Application_Constants';
import ExpenseCategoryType from './ExpenseCategoryType';
import ExpenseBillType from './ExpenseBillType';
import MiscExpenseDescription from './MiscExpenseDescription';
import ExpenseDescription from './ExpenseDescription';
import VendorPaymentStartDate from './VendorPaymentStartDate';
import VendorPaymentEndDate from './VendorPaymentEndDate';
import ExpenseClassEnum from './ExpenseClass.enum';

const styles = () => ({
    formHeader: {
        borderBottom: `1px solid ${COLORS.LT_MIDNIGHT25}`,
        paddingBottom: 8,
        marginBottom: 16,
    },
});

const VendorExpenseDetailsForm = (props) => {
    const { classes, index, expense, expenseCategories, originalExpenseEntries, isViewOnly, mode } =
        props;
    const { control, watch, errors } = useFormContext();
    const expenseCategory = watch(`expenseEntries[${index}].category`);
    const expenseType = watch(`expenseEntries[${index}].type`);
    const isEndDateRequired = expenseCategory === 'Travel' && expenseType === 'Rent';
    const isMiscExpense = expenseCategory === 'Misc' && expenseType === 'Misc';
    // Get the original status from the originalExpenseEntries
    const originalStatus = useMemo(() => {
        if (expense.id && originalExpenseEntries?.length > 0) {
            const originalExpense = originalExpenseEntries.find((entry) => entry.id === expense.id);
            return originalExpense ? originalExpense.status : expense.status;
        }
        return expense.status; // Fallback to current status if no original entry is found
    }, [expense.id, originalExpenseEntries, expense.status]);
    // Track the current status being edited
    const currentStatus = watch(`expenseEntries[${index}].status`);
    // Compute isPosted based on original status and current transient state
    const isPosted = useMemo(
        () =>
            originalStatus === 'Posted' ||
            (currentStatus === 'OnHoldReimbursement' && originalStatus === 'Posted'),
        [originalStatus, currentStatus]
    );
    const isPendingSubmission = expense.status === 'PendingSubmission';
    const expenseTypes = useMemo(() => {
        if (!expenseCategory) return [];
        if (!expenseCategories) return [];
        const { types } = expenseCategories.find((option) => option.category === expenseCategory);
        return types || [];
    }, [expenseCategory, expenseCategories]);

    const isPaymentRequest = expense.expenseClass == ExpenseClassEnum.Payment;

    return (
        <>
            <Typography variant="h5" component="h5" className={classes.formHeader}>
                Expense Details
            </Typography>
            <Grid2 container spacing={3}>
                <Grid2 item size={{ xs: 12, md: 12 }}>
                    <ExpenseBillType
                        index={index}
                        expense={expense}
                        isClinicianReimbursement={!isPaymentRequest}
                        isSGA={[]}
                        isPosted={isPosted}
                        isPendingSubmission={isPendingSubmission}
                        isViewOnly={isViewOnly}
                        mode={mode}
                    />
                </Grid2>
                <Grid2 size={{ md: 12 }}>
                    <ExpenseCategoryType
                        expenseTypes={expenseTypes}
                        index={index}
                        expense={expense}
                        expenseCategories={expenseCategories}
                        isSGA={[]}
                        isPosted={isPosted}
                        isPendingSubmission={isPendingSubmission}
                        isViewOnly={isViewOnly}
                        mode={mode}
                    />
                </Grid2>
                <Grid2 item size={{ md: 6 }}>
                    <VendorPaymentStartDate
                        expense={expense}
                        index={index}
                        specialLabel={
                            expenseCategory === 'Supplemental' && isPaymentRequest
                                ? 'Date'
                                : 'Start Date'
                        }
                        isViewOnly={props?.isViewOnly ?? false}
                    />
                </Grid2>

                {expenseCategory !== 'Supplemental' && (
                    <Grid2 item size={{ md: 6 }}>
                        <VendorPaymentEndDate
                            expense={expense}
                            index={index}
                            isRequired={isEndDateRequired}
                            isViewOnly={props?.isViewOnly ?? false}
                        />
                    </Grid2>
                )}

                {isMiscExpense && (
                    <Grid2 size={{ md: 10 }}>
                        <MiscExpenseDescription
                            index={index}
                            expense={expense}
                            isMiscExpense={isMiscExpense}
                            isViewOnly={props?.isViewOnly ?? false}
                        />
                    </Grid2>
                )}
                <Grid2 size={{ md: 12 }}>
                    <ExpenseDescription
                        index={index}
                        expense={expense}
                        isViewOnly={props?.isViewOnly ?? false}
                    />
                </Grid2>
            </Grid2>
        </>
    );
};

export default withStyles(styles)(VendorExpenseDetailsForm);
